import { useState, useCallback, Suspense, lazy } from 'react'
import { OpenInNewTab } from '@/components/ui/open-in-new-tab'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { formatDateForLocale } from '@/lib/dateUtils'
import { useRecoverySet, useMobilisationStatusBySet, useUnassignedRequestIDs, useRequestIdsInSet, useAddRequestToSetWithResolutions, useRemoveRequestIdFromSet, type ViewConflictRow } from '../hooks'
import { recoverySetsApi, recoverRequestsApi, type RequestObjectDetailRow } from '../api'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { OverflowTabsList } from '@/components/ui/overflow-tabs'
import { PostRecoveryTab } from '../post-recovery/PostRecoveryTab'
import { RecoveryMapTab } from '../components/location-tracking/RecoveryMapTab'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { MobilisationTab } from '../components/mobilisation'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { groupOf, viewForGroup, viewsOf, type ManagerGroup, type ManagerView } from '../recoveryManagerTabs'
import { RequestIdsKanban } from '../components/RequestIdsKanban'
import { ConflictResolutionModal } from '../components/ConflictResolutionModal'
import { RecoveryManifestDialog } from '../components/RecoveryManifestDialog'
import { ManifestStatusBadge, useLatestManifest } from '../components/ManifestStatus'
import { RecoverySetAllObjectsDialog } from '../components/RecoverySetAllObjectsDialog'
import { RecoverySetObjectsTable } from '../components/RecoverySetObjectsTable'
import { RunbookViewDialog } from '../components/RunbookViewDialog'
import { TickerSampleActions } from '@/features/ticker/components/TickerSampleActions'
// Lazy, like the canvas below it: the chart pulls in the scheduler and its own SVG layer, and
// most visits to this page never open the tab.
const RecoveryGanttTab = lazy(() => import('../components/RecoveryGanttTab'))
import { getStatusDotClass, getStatusPillClass } from '../components/RecoveryTrackingHeader'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ArrowLeft, ChevronRight, LayoutDashboard, Radio, ListOrdered, Notebook, Network, Maximize2, Minimize2, FileText, Link2 } from 'lucide-react'
import { LocationTrackingTab } from '../components/location-tracking/LocationTrackingTab'
import { ManagementUpdatesTab } from '../components/ManagementUpdatesTab'
import { cn } from '@/lib/utils'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { sanitizeRichText } from '@/lib/sanitize-html'
import { useCan } from '@/hooks/useCan'

const VisualizationApp = lazy(() => import('../../visualization/_legacy/App'))
const AppStateProvider = lazy(() =>
    import('../../visualization/_legacy/context/AppStateContext').then((m) => ({
        default: m.AppStateProvider,
    }))
)

const PIPELINE_STAGES = [
    { key: 'unassigned', labelKey: 'recoverRequests.unassigned', labelFallback: 'Unassigned', hintKey: 'recoverRequests.awaitingEngineer', hintFallback: 'Awaiting engineer', cardClass: 'bg-muted/60 border-muted', numberClass: 'text-foreground' },
    { key: 'assigned', labelKey: 'recoverRequests.assigned', labelFallback: 'Assigned', hintKey: 'recoverRequests.engineerAllocated', hintFallback: 'Engineer allocated', cardClass: 'bg-blue-500/15 border-blue-500/30 dark:bg-blue-500/20', numberClass: 'text-blue-700 dark:text-blue-300' },
    { key: 'inProgress', labelKey: 'recoverRequests.inProgress', labelFallback: 'In progress', hintKey: 'recoverRequests.workUnderway', hintFallback: 'Work underway', cardClass: 'bg-amber-500/15 border-amber-500/30 dark:bg-amber-500/20', numberClass: 'text-amber-700 dark:text-amber-300' },
    { key: 'completed', labelKey: 'recoverRequests.completed', labelFallback: 'Completed', hintKey: 'recoverRequests.verifiedRecovered', hintFallback: 'Verified recovered', cardClass: 'bg-green-500/15 border-green-500/30 dark:bg-green-500/20', numberClass: 'text-green-700 dark:text-green-300' },
] as const

function RecoveryCompletionBar({ completed, total }: { completed: number; total: number }) {
    const { t } = useTranslation()
    const pct = total > 0 ? Math.min(100, Math.max(0, (completed / total) * 100)) : 0
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
                <h2 className="font-semibold text-foreground">{t('recoverRequests.recoveryCompletion', 'Recovery completion')}</h2>
                <span className="text-sm text-muted-foreground tabular-nums">
                    {completed} / {total} {t('recoverRequests.completedCountLabel', 'completed')}
                </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    )
}

function RecoveryPipelineGrid({
    stages,
    values,
}: {
    stages: readonly { key: string; labelKey: string; labelFallback: string; hintKey: string; hintFallback: string; cardClass: string; numberClass: string }[]
    values: { unassigned: number; assigned: number; inProgress: number; completed: number }
}) {
    const { t } = useTranslation()
    return (
        <div className="flex flex-wrap items-stretch gap-0">
            {stages.map((stage, i) => (
                <div key={stage.key} className="flex items-stretch gap-0">
                    <div
                        className={cn(
                            'rounded-lg border py-4 px-4 min-w-[100px] text-center flex flex-col justify-center',
                            stage.cardClass
                        )}
                    >
                        <div className={cn('text-2xl font-bold tabular-nums', stage.numberClass)}>
                            {values[stage.key as keyof typeof values]}
                        </div>
                        <div className="text-sm font-medium text-foreground">{t(stage.labelKey, stage.labelFallback)}</div>
                        <div className="text-xs text-muted-foreground">{t(stage.hintKey, stage.hintFallback)}</div>
                    </div>
                    {i < stages.length - 1 && (
                        <div className="flex flex-col items-center justify-center self-center px-1 shrink-0">
                            <span
                                className="flex h-6 w-6 items-center justify-center rounded-full border border-muted-foreground/40 bg-background shrink-0"
                                aria-hidden
                            >
                                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                            </span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

function RecoveryIssuesRow({ count }: { count: number }) {
    const { t } = useTranslation()
    const hasIssues = count > 0
    return (
        <div
            className={cn(
                'flex items-center justify-between rounded-lg border px-3 py-2.5 transition-colors duration-200',
                hasIssues
                    ? 'border-destructive/50 bg-destructive/10 text-destructive'
                    : 'border-muted bg-muted/30 text-muted-foreground'
            )}
        >
            <span className="flex items-center gap-2 text-sm">
                <span
                    className={cn('h-1.5 w-1.5 shrink-0 rounded-full', hasIssues ? 'bg-destructive' : 'bg-muted-foreground')}
                    aria-hidden
                />
                {t('recoverRequests.blockedIssues', 'Blocked / issues')}
            </span>
            <span className={cn('text-xl font-bold tabular-nums', hasIssues ? 'text-destructive' : 'text-muted-foreground')}>{count}</span>
        </div>
    )
}

export function RecoverySetDetailPage() {
    const { t, i18n } = useTranslation()
    const { setId: setIdParam } = useParams<{ setId: string }>()
    const navigate = useNavigate()
    const setId = setIdParam ? parseInt(setIdParam, 10) : NaN
    const isValidSet = !isNaN(setId) && setId > 0

    const { data: set, isLoading: setLoading } = useRecoverySet(isValidSet ? setId : null)
    const { data: mobilisationStatus } = useMobilisationStatusBySet(isValidSet ? setId : null)
    const mobilisationCompleted = mobilisationStatus?.completedSteps ?? 0
    const mobilisationTotal = mobilisationStatus?.totalSteps ?? 3
    const { data: unassignedData } = useUnassignedRequestIDs({ page: 1, limit: 100 })
    const unassignedList = unassignedData?.list ?? []
    const { data: inSetList = [], isLoading: inSetLoading } = useRequestIdsInSet(isValidSet ? setId : null)
    const addWithResolutions = useAddRequestToSetWithResolutions(isValidSet ? setId : null)
    const removeFromSet = useRemoveRequestIdFromSet(isValidSet ? setId : null)
    // The page is open to recover-requests.read. Changing which requests are in the set is
    // managing it (recover-requests.update); the set's pre-signed URLs release recovery data
    // (recovery.data). Neither is offered without its permission.
    const canManage = useCan('recover-requests.update')
    const canRelease = useCan('recovery.data')
    const { toast } = useToast()
    const userId = useAuthStore((s) => s.user?.id ?? null) as number | null
    const [isChecking, setIsChecking] = useState(false)
    const locale = i18n.language || 'en-US'
    const [conflictModal, setConflictModal] = useState<{ open: boolean; requestId: string; conflicts: ViewConflictRow[]; incomingRequestLabel?: string }>({
        open: false,
        requestId: '',
        conflicts: [],
    })
    const [allObjectsDialogOpen, setAllObjectsDialogOpen] = useState(false)
    const [objectRunbookDialog, setObjectRunbookDialog] = useState<{ objectID: string; forecastStartTime: string | null } | null>(null)
    /** The manifest: generate the set's pre-signed URLs and mint the one link for the PXE console. */
    const [manifestDialogOpen, setManifestDialogOpen] = useState(false)
    /** Bumped when the dialog closes — building in there is what changes the badge out here. */
    const [manifestRefresh, setManifestRefresh] = useState(0)
    const { manifest: setManifestState } = useLatestManifest(
        isValidSet ? `recoverySet=${setId}` : null, manifestRefresh
    )
    const [updatesDialogRecoverRequestId, setUpdatesDialogRecoverRequestId] = useState<number | null>(null)
    const [visFullscreen, setVisFullscreen] = useState(false)
    const [activeTab, setActiveTab] = useState('dashboard')
    // The menu is five groups; each remembers the view last used in it (recoveryManagerTabs.ts).
    const [lastViewOfGroup, setLastViewOfGroup] = useState<Partial<Record<ManagerGroup, ManagerView>>>({})
    const activeGroup = groupOf(activeTab)
    const groupViews = viewsOf(activeGroup)
    const selectView = useCallback((view: string) => {
        setActiveTab(view)
        setLastViewOfGroup((last) => ({ ...last, [groupOf(view)]: view as ManagerView }))
    }, [])
    const selectGroup = useCallback((group: string) => selectView(viewForGroup(group, lastViewOfGroup)), [selectView, lastViewOfGroup])
    const viewLabels: Record<ManagerView, string> = {
        dashboard: t('recoverRequests.mobilisation.dashboard', 'Dashboard'),
        mobilisation: t('recoverRequests.mobilisation.tabTitle', 'Mobilisation'),
        'request-ids': t('recoverRequests.mobilisation.requestIds', 'Request IDs'),
        'recovery-gantt': t('recoverRequests.ganttTab', 'Gantt'),
        visualization: t('recoverRequests.canvasTab', 'Canvas'),
        objects: t('recoverRequests.mobilisation.tracking', 'Tracking'),
        'location-tracking': t('recoverRequests.locationTracking.tab', 'Location Tracking'),
        'recovery-map': t('recoverRequests.recoveryMap.tab', 'Map'),
        'management-updates': t('recoverRequests.managementUpdates', 'Updates'),
        'post-recovery': t('recoverRequests.postRecovery.tab', 'Post Recovery'),
    }
    /**
     * The main node, shared by the Canvas and Gantt tabs.
     *
     * ONE variable, deliberately. They are two readings of the same recovery, so choosing an
     * object on either should leave the other already showing it — two independent selections
     * would silently diverge and each tab would be answering about a different object.
     */
    const [mainNodeId, setMainNodeId] = useState<string | null>(null)

    const { data: setObjects = [], isLoading: setObjectsLoading, isError: setObjectsError } = useQuery({
        queryKey: ['recovery-set', 'set-objects', setId],
        queryFn: async () => {
            const res = await recoverySetsApi.getSetObjectDetails(setId)
            const payload = (res.data as { data?: RequestObjectDetailRow[] })?.data
            return payload ?? []
        },
        enabled: isValidSet && setId > 0,
    })

    const handleAddToSet = useCallback(
        async (payload: { requestID: string; scenario?: number | null; objectID?: string | null; objectDescription?: string | null; forecastStartTime?: string | null }) => {
            if (!isValidSet || !payload.requestID) return
            setIsChecking(true)
            try {
                const res = await recoverySetsApi.checkAddRequestConflicts(setId, payload.requestID)
                const data = (res.data as { data?: { status: string; conflicts?: ViewConflictRow[] } })?.data
                if (data?.status === 'conflicts' && Array.isArray(data.conflicts) && data.conflicts.length > 0) {
                    const dateLabel = payload.forecastStartTime
                        ? formatDateForLocale(payload.forecastStartTime, locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : ''
                    const incomingRequestLabel = payload.objectID ? (dateLabel ? `${payload.objectID} – ${dateLabel}` : payload.objectID) : undefined
                    setConflictModal({ open: true, requestId: payload.requestID, conflicts: data.conflicts, incomingRequestLabel })
                    return
                }
                await addWithResolutions.mutateAsync({
                    requestId: payload.requestID,
                    resolutions: [],
                    userId: userId ?? undefined,
                })
            } catch (err) {
                const msg = err instanceof Error ? err.message : 'Failed to add request to set'
                toast({ variant: 'destructive', title: 'Error', description: msg })
            } finally {
                setIsChecking(false)
            }
        },
        [isValidSet, setId, userId, addWithResolutions, toast, locale]
    )

    const isEngineerAssigned = useCallback((row: RequestObjectDetailRow) => {
        const status = (row.assignmentStatus ?? '').toLowerCase()
        const hasEngineer = !!(row.engineerName ?? '').trim()
        return hasEngineer || status === 'assigned' || status === 'inprogress' || status === 'in progress'
    }, [])

    const handleViewRunbook = useCallback(
        (row: RequestObjectDetailRow) => {
            const objectID = row.objectID ?? ''
            if (!objectID) return
            if (isEngineerAssigned(row) && row.recoverRequestID != null && row.recoverRequestID > 0) {
                navigate(`/recovery-runbook/${encodeURIComponent(row.recoverRequestID)}`, {
                    state: {
                        objectID: row.objectID,
                        objectDescription: row.objectDescription,
                        forecastStartTime: row.forecastStartTime,
                        from: `/recovery-management/sets/${setId}`,
                        readOnly: true,
                    },
                })
            } else {
                setObjectRunbookDialog({
                    objectID,
                    forecastStartTime: row.forecastStartTime ?? null,
                })
            }
        },
        [navigate, setId, isEngineerAssigned]
    )

    const { data: updatesData } = useQuery({
        queryKey: ['recovery-assignments', updatesDialogRecoverRequestId],
        queryFn: async () => {
            const idToFetch = updatesDialogRecoverRequestId ?? ''
            if (!idToFetch) return []
            const res = await recoverRequestsApi.getRecoveryAssignments(String(idToFetch))
            const body = res?.data ?? res
            if (!body) return []
            const inner = body.data ?? body
            const list = Array.isArray(inner) ? inner : (inner?.assignments ?? [])
            return Array.isArray(list) ? list : []
        },
        enabled: updatesDialogRecoverRequestId != null && updatesDialogRecoverRequestId > 0,
        refetchOnMount: true,
    })
    const updatesList = Array.isArray(updatesData) ? updatesData : []

    function formatDateShort(date: Date | string): string {
        const d = typeof date === 'string' ? new Date(date) : date
        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
    }

    if (!isValidSet) {
        return (
            <div className="p-6 space-y-4">
                <p className="text-muted-foreground">{t('recoverRequests.errorLoading', 'Invalid set.')}</p>
                <Button variant="outline" onClick={() => navigate('/recovery-management')}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t('common.back')}
                </Button>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-4">
                <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" className="w-fit" onClick={() => navigate('/recovery-management')}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('common.back')}
                    </Button>
                    <h1 className="text-2xl font-bold">{t('recoverRequests.recoveryManager')}</h1>
                    <div className="ml-auto"><OpenInNewTab /></div>
                </div>
                <Card className="rounded-lg border bg-card">
                    <CardContent className="p-4 flex flex-row items-center justify-between gap-4 flex-wrap">
                        <div className="flex flex-col gap-1 min-w-0 flex-1">
                            <h1 className="text-xl font-bold truncate text-foreground">
                                {setLoading ? t('common.loading', 'Loading…') : (set?.name ?? '') || '—'}
                            </h1>
                            {(set?.startTime != null || set?.completedTime != null || set?.rolledUpRtaText != null) && (
                                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 text-sm">
                                    {set?.startTime != null && (
                                        <span>
                                            <span className="font-medium text-muted-foreground">{t('recoverRequests.forecastStartTime', 'Forecast start')}</span>{' '}
                                            <span className="text-foreground font-medium">{formatDateForLocale(set.startTime, locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                        </span>
                                    )}
                                    {set?.completedTime != null && (
                                        <span>
                                            <span className="font-medium text-muted-foreground">{t('recoverRequests.forecastFinishTime', 'Forecast finish')}</span>{' '}
                                            <span className="text-foreground font-medium">{formatDateForLocale(set.completedTime, locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                        </span>
                                    )}
                                    {set?.rolledUpRtaText != null && (
                                        <span>
                                            <span className="font-medium text-muted-foreground">{t('recoverRequests.rolledUpRta', 'Rolled-up RTA')}</span>{' '}
                                            <span className="text-foreground font-medium">{set.rolledUpRtaText}</span>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            {/* Development only, as system templates are: renders nothing
                                where shipped content is not authored (Andrew, 25 Sep 2026). */}
                            <TickerSampleActions setId={setId} />
                            <div className="flex flex-col items-end gap-1">
                                {canRelease && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setManifestDialogOpen(true)}
                                        disabled={!isValidSet}
                                    >
                                        <Link2 className="h-4 w-4 mr-2" />
                                        {t('recoverRequests.recoveryManifest', 'Pre-signed URLs')}
                                    </Button>
                                )}
                                {/* Whether one already exists and when its link dies, without
                                    opening the dialog to find out. */}
                                <ManifestStatusBadge manifest={setManifestState} showEmpty />
                            </div>
                            {set?.runbookPercentComplete != null && (() => {
                                const size = 72
                                const stroke = 5
                                const r = (size - stroke) / 2
                                const circumference = 2 * Math.PI * r
                                const pct = Math.min(100, Math.max(0, set.runbookPercentComplete))
                                const dash = (pct / 100) * circumference
                                return (
                                    <div className="flex items-center gap-3">
                                        <div className="relative shrink-0" style={{ width: size, height: size }} aria-hidden>
                                            <svg width={size} height={size} className="rotate-[-90deg]">
                                                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-muted/30" />
                                                <circle
                                                    cx={size / 2}
                                                    cy={size / 2}
                                                    r={r}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={stroke}
                                                    strokeDasharray={`${dash} ${circumference - dash}`}
                                                    strokeLinecap="round"
                                                    className="text-primary transition-all duration-300"
                                                />
                                            </svg>
                                            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums">
                                                {Math.round(pct)}%
                                            </span>
                                        </div>
                                        <div className="flex flex-col items-start">
                                            <span className="text-sm font-semibold tabular-nums">{Math.round(pct)}%</span>
                                            <span className="text-xs text-muted-foreground">{t('recoverRequests.runbookProgress', 'Runbook')}</span>
                                        </div>
                                    </div>
                                )
                            })()}
                        </div>
                    </CardContent>
                </Card>
            </div>
            <RecoverySetAllObjectsDialog
                open={allObjectsDialogOpen}
                onOpenChange={setAllObjectsDialogOpen}
                setId={setId}
                setName={set?.name ?? undefined}
            />

            {/* Generate every pre-signed URL in this set and mint the single link the
                engineer pastes into the PXE recovery server console. */}
            <RecoveryManifestDialog
                open={manifestDialogOpen}
                onOpenChange={(o) => { setManifestDialogOpen(o); if (!o) setManifestRefresh((n) => n + 1) }}
                recoverySet={isValidSet ? setId : null}
            />

            {/* The menu: five groups by phase of the incident, each with its views (recoveryManagerTabs.ts).
                The bar has its own Tabs root because its values are GROUPS; the content below is
                keyed by VIEW, and a trigger in the same root would set the view to a group's name. */}
            <Tabs value={activeGroup} onValueChange={selectGroup} className="w-full">
                <OverflowTabsList
                    value={activeGroup}
                    onValueChange={selectGroup}
                    items={[
                        { value: 'dashboard', icon: <LayoutDashboard className="h-4 w-4" />, label: t('recoverRequests.mobilisation.dashboard', 'Dashboard') },
                        { value: 'assess', icon: <Radio className="h-4 w-4" />, label: t('recoverRequests.mobilisation.tabTitle', 'Mobilisation') },
                        { value: 'plan', icon: <ListOrdered className="h-4 w-4" />, label: t('recoverRequests.managerGroups.plan', 'Plan') },
                        { value: 'recover', icon: <Network className="h-4 w-4" />, label: t('recoverRequests.managerGroups.recover', 'Recover') },
                        { value: 'report', icon: <FileText className="h-4 w-4" />, label: t('recoverRequests.managerGroups.report', 'Report') },
                    ]}
                />
            </Tabs>
            {groupViews.length > 1 && (
                <div className="mt-3" data-testid="manager-views">
                    <SegmentedControl<ManagerView>
                        value={activeTab as ManagerView}
                        onValueChange={selectView}
                        ariaLabel={t('recoverRequests.managerGroups.views', 'Views')}
                        options={groupViews.map((view) => ({ value: view, label: viewLabels[view] }))}
                    />
                </div>
            )}

            <Tabs value={activeTab} onValueChange={selectView} className="w-full">
                <TabsContent value="dashboard" className="mt-4">
                    {setLoading ? (
                        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                            {t('common.loading', 'Loading…')}
                        </div>
                    ) : set ? (
                        <div className="space-y-6">
                            {/* Recovery completion, pipeline and issues — data from recovery set */}
                            {(() => {
                                const totalRequests = set.total ?? 0
                                const assigned = set.assigned ?? 0
                                const inProgress = set.inProgress ?? 0
                                const issues = set.issue ?? 0
                                const completed = set.completed ?? 0
                                const unassigned = Math.max(0, totalRequests - assigned - inProgress - issues - completed)
                                const sum = unassigned + assigned + inProgress + issues + completed
                                if (import.meta.env.DEV && totalRequests > 0 && sum !== totalRequests) {
                                    console.warn(
                                        '[Recovery Set Dashboard] Pipeline counts do not sum to totalRequests:',
                                        { totalRequests, unassigned, assigned, inProgress, issues, completed, sum }
                                    )
                                }
                                return (
                                    <>
                                        {/* Recovery completion — no border */}
                                        <RecoveryCompletionBar completed={completed} total={totalRequests} />
                                        {/* Pipeline + Blocked issues — same width as the four cards */}
                                        <div className="w-fit flex flex-col gap-4">
                                            <div className="space-y-3">
                                                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                                    {t('recoverRequests.currentPipeline', 'Current pipeline')}
                                                </p>
                                                <RecoveryPipelineGrid
                                                    stages={PIPELINE_STAGES}
                                                    values={{ unassigned, assigned, inProgress, completed }}
                                                />
                                            </div>
                                            <div className="w-full min-w-0">
                                                <RecoveryIssuesRow count={issues} />
                                            </div>
                                        </div>
                                    </>
                                )
                            })()}
                            <Card>
                                <CardHeader>
                                    <span className="font-medium">{t('recoverRequests.mobilisation.progress', 'Mobilisation progress')}</span>
                                    <span className="text-muted-foreground text-sm tabular-nums">
                                        {mobilisationCompleted}/{mobilisationTotal} {t('recoverRequests.mobilisation.stepsComplete', 'steps complete')}
                                    </span>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                                        <div
                                            className="h-full bg-primary rounded-full transition-all"
                                            style={{ width: `${(mobilisationCompleted / mobilisationTotal) * 100}%` }}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <span className="font-medium">{t('recoverRequests.technicalChecklistSummary', 'Technical checklist summary')}</span>
                                </CardHeader>
                                <CardContent className="text-muted-foreground text-sm">
                                    {t('recoverRequests.technicalChecklistSummaryPlaceholder', 'Per-team checklist status will appear here when available.')}
                                </CardContent>
                            </Card>
                        </div>
                    ) : (
                        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                            {t('recoverRequests.setNotFound', 'Set not found.')}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="mobilisation" className="mt-4">
                    <MobilisationTab setId={setId} totalSteps={3} />
                </TabsContent>

                <TabsContent value="request-ids" className="mt-4">
                    <RequestIdsKanban
                        unassignedList={unassignedList}
                        inSetList={inSetList}
                        inSetLoading={inSetLoading}
                        onAddToSet={handleAddToSet}
                        onRemoveFromSet={(requestID) => removeFromSet.mutate(requestID)}
                        isAdding={isChecking || addWithResolutions.isPending}
                        isRemoving={removeFromSet.isPending}
                        setId={setId}
                        onViewAllObjects={() => setAllObjectsDialogOpen(true)}
                        readOnly={!canManage}
                    />
                    <ConflictResolutionModal
                        open={conflictModal.open}
                        onOpenChange={(open) => setConflictModal((prev) => ({ ...prev, open }))}
                        requestId={conflictModal.requestId}
                        incomingRequestLabel={conflictModal.incomingRequestLabel}
                        recoverySetName={set?.name ?? undefined}
                        conflicts={conflictModal.conflicts}
                        onSubmit={(resolutions) =>
                            addWithResolutions.mutate({
                                requestId: conflictModal.requestId,
                                resolutions,
                                userId: userId ?? undefined,
                            })
                        }
                        isSubmitting={addWithResolutions.isPending}
                    />
                </TabsContent>

                <TabsContent value="visualization" className={visFullscreen ? "fixed inset-0 z-50 bg-background p-0 mt-0" : "mt-4"}>
                    <div className={visFullscreen ? "h-screen w-screen" : "h-[calc(100vh-240px)] min-h-[700px] w-full rounded-lg border bg-muted/30 overflow-hidden"} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setVisFullscreen(!visFullscreen)}
                            className="absolute top-2 left-2 z-[2000] bg-white border border-gray-300 rounded-md p-1.5 shadow-md hover:bg-gray-100"
                            title={visFullscreen ? "Exit full screen" : "Full screen"}
                        >
                            {visFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </button>
                        {activeTab === 'visualization' && (
                            <Suspense fallback={
                                <div className="flex h-full items-center justify-center">
                                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                                </div>
                            }>
                                <AppStateProvider>
                                    <ReactFlowProvider>
                                        <VisualizationApp
                                            scenario="recovery"
                                            view="dependency"
                                            requestId={inSetList[0]?.requestID || ''}
                                            recoverySetId={setId}
                                            showMainPalette={true}
                                            onMainNodeChange={setMainNodeId}
                                            objectId={mainNodeId || undefined}
                                            scenarioId={String(inSetList[0]?.scenario || '1')}
                                            serviceId={String(inSetList[0]?.scenario || '1')}
                                            // Restrict the main-node palette to the objects that belong to this
                                            // recovery set. Pass undefined while still loading so we don't
                                            // momentarily hide everything before the set objects arrive.
                                            mainNodeIds={setObjectsLoading ? undefined : Array.from(new Set(setObjects.map((o) => o.objectID).filter((x): x is string => !!x)))}
                                        />
                                    </ReactFlowProvider>
                                </AppStateProvider>
                            </Suspense>
                        )}
                    </div>
                </TabsContent>

                {/* Mounted only while selected, for the same reason as the canvas: it polls,
                    and a hidden chart polling a live run is cost with no reader. */}
                <TabsContent value="recovery-gantt" className="mt-4">
                    <div className="h-[calc(100vh-240px)] min-h-[700px] w-full overflow-hidden rounded-lg border bg-background">
                        {activeTab === 'recovery-gantt' && isValidSet && (
                            <Suspense fallback={
                                <div className="flex h-full items-center justify-center">
                                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                                </div>
                            }>
                                <RecoveryGanttTab
                                    setId={setId}
                                    mainNodeId={mainNodeId}
                                    onSelectMainNode={setMainNodeId}
                                />
                            </Suspense>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="objects" className="mt-4">
                    <RecoverySetObjectsTable
                        rows={setObjects}
                        isLoading={setObjectsLoading}
                        isError={setObjectsError}
                        inCard
                        locale={locale}
                        onViewRunbook={handleViewRunbook}
                        onOpenUpdates={setUpdatesDialogRecoverRequestId}
                    />
                    {objectRunbookDialog && (
                        <RunbookViewDialog
                            open={!!objectRunbookDialog}
                            onOpenChange={(open) => !open && setObjectRunbookDialog(null)}
                            objectID={objectRunbookDialog.objectID}
                            scenario={null}
                            forecastStartTime={objectRunbookDialog.forecastStartTime}
                        />
                    )}
                    <Dialog open={updatesDialogRecoverRequestId != null} onOpenChange={(open) => !open && setUpdatesDialogRecoverRequestId(null)}>
                        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Notebook className="h-5 w-5 text-blue-600" />
                                    {t('recoverRequests.updates', 'Updates')}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="overflow-auto flex-1 min-h-0 -mx-6 px-6">
                                {updatesList.length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-4">{t('recoverRequests.noRecoveryUpdates', 'No recovery updates yet.')}</p>
                                ) : (
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="border-b bg-muted/50">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 w-8" scope="col">Status</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap" scope="col">Action</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap w-36" scope="col">Date & time</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap" scope="col">User</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 min-w-[8rem]" scope="col">{t('recoverRequests.engineerUpdate', 'Engineer update')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {updatesList.map((u: { id?: string; action?: string; actiondatetime?: string; userName?: string; update?: string }) => {
                                                const dotClass = getStatusDotClass(u.action ?? '')
                                                const pillClass = getStatusPillClass(u.action ?? '')
                                                return (
                                                    <tr key={u.id ?? `${u.actiondatetime}-${u.userName}`} className="border-b last:border-0">
                                                        <td className="py-2 px-3 align-middle">
                                                            <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${dotClass}`} aria-hidden="true" />
                                                        </td>
                                                        <td className="py-2 px-3">
                                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${pillClass}`}>{u.action ?? '—'}</span>
                                                        </td>
                                                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{u.actiondatetime ? formatDateShort(u.actiondatetime) : '—'}</td>
                                                        <td className="py-2 px-3 text-gray-600">{u.userName ?? '—'}</td>
                                                        <td className="py-2 px-3 text-gray-700">
                                                            {u.update ? (
                                                                <div className="prose prose-sm max-w-none [&_ul]:list-disc [&_ol]:list-decimal [&_p]:mb-1 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: sanitizeRichText(u.update) }} />
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>
                </TabsContent>

                <TabsContent value="location-tracking" className="mt-4">
                    {isValidSet && <LocationTrackingTab setId={setId} />}
                </TabsContent>

                <TabsContent value="management-updates" className="mt-4">
                    <ManagementUpdatesTab setId={setId!} setName={set?.name ?? ''} />
                </TabsContent>

                {/* Its own tab rather than sitting above the clocks: the map is a different
                    question from "what time is it there", and a 340px canvas on every visit
                    to Location Tracking pushed the columns people actually read below the
                    fold. */}
                <TabsContent value="recovery-map" className="mt-4">
                    <RecoveryMapTab setId={setId!} />
                </TabsContent>
                {/* Mounted only while selected: the post-recovery views run several
                    aggregate queries, and a set that is still being recovered should not pay
                    for them on every visit to the dashboard. */}
                <TabsContent value="post-recovery" className="mt-4">
                    {activeTab === 'post-recovery' && isValidSet && <PostRecoveryTab setId={setId!} />}
                </TabsContent>
            </Tabs>
        </div>
    )
}
